import { MaterialCommunityIcons } from "@expo/vector-icons";
import type { MergedPocDuty, PocDutyChangeReason, PocFrequency } from "@taskhealth/shared";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect } from "expo-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, TextInput, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { PocDutyList } from "./PocDutyList";
import { PocEmptyState } from "./PocEmptyState";
import { PocGeneratingAnimation } from "./PocGeneratingAnimation";

import { SignatureModal } from "@/components/questions/SignatureModal";
import { usePollWhileProcessing } from "@/hooks/usePollWhileProcessing";
import {
    ApiError,
    confirmPoc,
    confirmPocUpdateAssessment,
    fetchExistingPoc,
    generatePocDuties,
    type PocMode as PocModeInfo,
    type PocUpdateAssessment,
    savePocDuties
} from "@/lib/api";
import { colors, fonts, gradients, radius, shadows, spacing } from "@/lib/theme";
import { useWebSocket, useWebSocketEvent } from "@/lib/websocket";

// ---------------------------------------------------------------------------
// Props — `serviceType` was accepted but never read; removed.
// ---------------------------------------------------------------------------

interface Props {
    visitId: string;
    sectionId: string;
    sectionDisplayName: string;
    onComplete: () => void;
    onBack: () => void;
    readOnly: boolean;
}

type Phase = "loading" | "idle" | "stale" | "generating" | "ready" | "notRequired" | "error";
export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

type PocDutySnapshot = {
    name: string;
    selected: boolean;
    frequency: string;
    rnChangeReasonCodes: PocDutyChangeReason[];
    rnChangeReasonNote: string | null;
};

function dutyKey(duty: MergedPocDuty): string {
    return duty.id != null ? `id:${duty.id}` : `name:${duty.name}`;
}

function normalizedReasons(duty: MergedPocDuty): PocDutyChangeReason[] {
    return [...(duty.rnChangeReasonCodes ?? [])].sort();
}

function toDutySnapshot(duty: MergedPocDuty): PocDutySnapshot {
    return {
        name: duty.name,
        selected: duty.selected,
        frequency: duty.frequency,
        rnChangeReasonCodes: normalizedReasons(duty),
        rnChangeReasonNote: duty.rnChangeReasonNote?.trim() || null
    };
}

function dutySnapshotsEqual(a: PocDutySnapshot, b: PocDutySnapshot): boolean {
    return (
        a.name === b.name &&
        a.selected === b.selected &&
        a.frequency === b.frequency &&
        a.rnChangeReasonNote === b.rnChangeReasonNote &&
        a.rnChangeReasonCodes.length === b.rnChangeReasonCodes.length &&
        a.rnChangeReasonCodes.every((code, index) => code === b.rnChangeReasonCodes[index])
    );
}

function isDutyOverridden(duty: MergedPocDuty): boolean {
    return (
        duty.aiSuggested !== duty.selected ||
        (duty.agencyDefaultFrequency != null && duty.frequency !== duty.agencyDefaultFrequency)
    );
}

function hasDutyChangeExplanation(duty: MergedPocDuty): boolean {
    return (
        (duty.rnChangeReasonCodes?.length ?? 0) > 0 ||
        (duty.rnChangeReasonNote?.trim().length ?? 0) > 0
    );
}

// ---------------------------------------------------------------------------
// PocReviewStep Orchestrator
// ---------------------------------------------------------------------------

export function PocReviewStep({
    visitId,
    sectionId,
    sectionDisplayName,
    onComplete,
    onBack,
    readOnly
}: Props) {
    const [phase, setPhase] = useState<Phase>("loading");
    // Wall-clock epoch when generation started. PocGeneratingAnimation derives
    // the phase pill from elapsed time, so background→foreground transitions
    // resolve to the correct phase instantly instead of dump-firing setTimeout
    // queues. Null when not generating.
    const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [pocId, setPocId] = useState<string | null>(null);
    const [duties, setDuties] = useState<MergedPocDuty[]>([]);
    const [specialInstructions, setSpecialInstructions] = useState("");
    const [pocUpdateAssessment, setPocUpdateAssessment] = useState<PocUpdateAssessment | null>(
        null
    );
    const [pocUpdateReason, setPocUpdateReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [sigModalVisible, setSigModalVisible] = useState(false);

    // Confirm-failure retry state.
    const [pendingSignature, setPendingSignature] = useState<string | null>(null);
    const [confirmError, setConfirmError] = useState<{
        message: string;
        retryable: boolean;
    } | null>(null);

    // Set from GET /poc once a plan exists. Only the duty list reads it, to
    // label a carried-forward plan as the current plan rather than as an AI
    // suggestion. Null while there is no plan yet — nothing depends on it then.
    const [pocMode, setPocMode] = useState<PocModeInfo | null>(null);

    const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>("idle");
    const readOnlyReason =
        "Visit completed - view only. Reopen the visit for resubmission to edit duties or notes.";

    // ── Race + lifecycle guards ──
    // mountedRef: WS callbacks fire on the module-level singleton even after
    // this component unmounts. Every cross-async setState gates on this.
    const mountedRef = useRef(true);
    // loadPocEpochRef: focus refresh + WS poc:generated + WS poc:failed + WS
    // reconnect can all fire loadPoc() concurrently. Two in-flight fetches can
    // return in reverse order; whichever lands last would otherwise stomp the
    // user's local edits. Every loadPoc captures `currentEpoch`; only the call
    // whose epoch still matches commits state.
    const loadPocEpochRef = useRef(0);
    const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autosaveEpochRef = useRef(0);

    // Snapshot of last server-confirmed state. Local edits are "dirty" when
    // they don't value-equal this snapshot. Value-equality (not a bool flag)
    // lets dirty self-clear when the RN reverts every edit.
    const savedSnapshotRef = useRef<{
        duties: PocDutySnapshot[];
        specialInstructions: string;
    } | null>(null);

    // Baseline for the "RN Override" badge. Selection comes from the LLM;
    // frequency comes from the agency default frozen at POC generation.
    const aiBaselineRef = useRef<Map<string, { selected: boolean; frequency: string }> | null>(
        null
    );
    const aiBaselineForPocIdRef = useRef<string | null>(null);

    // Refs that mirror state — used inside the autosave drift check so it
    // compares the IN-FLIGHT snapshot against the LATEST state at completion
    // time, not against the closure-captured values from when the autosave
    // started.
    const dutiesRef = useRef(duties);
    dutiesRef.current = duties;
    const specialInstructionsRef = useRef(specialInstructions);
    specialInstructionsRef.current = specialInstructions;

    // Unmount cleanup: flip mounted flag + clear pending autosave timer.
    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
            }
        };
    }, []);

    // ── isDirty helper ──
    // Captured via `isDirtyRef` (below) so callers — loadPoc in particular —
    // don't take a per-render dependency on its identity. Without the ref,
    // loadPoc's deps would include isDirty, which depends on [duties,
    // specialInstructions], so every keystroke would re-memoize loadPoc and
    // re-fire useFocusEffect's body → one GET /poc per keystroke.
    const isDirty = useCallback((): boolean => {
        const snap = savedSnapshotRef.current;
        if (!snap) {
            return false;
        }
        if (snap.specialInstructions !== specialInstructions) {
            return true;
        }
        if (snap.duties.length !== duties.length) {
            return true;
        }
        for (let i = 0; i < duties.length; i++) {
            const a = toDutySnapshot(duties[i]);
            const b = snap.duties[i];
            if (!dutySnapshotsEqual(a, b)) {
                return true;
            }
        }
        return false;
    }, [duties, specialInstructions]);

    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    // ── Refetch the POC ──
    // Returns the resolved status and the epoch this call ran under. Callers
    // MUST compare the returned epoch to loadPocEpochRef.current before
    // setting phase — otherwise a stale closure can flip phase backward.
    const loadPoc = useCallback(async (): Promise<{
        status: "pending" | "generated" | "failed" | "not_required" | "stale" | "none";
        epoch: number;
    }> => {
        const epoch = ++loadPocEpochRef.current;
        let existing;
        try {
            existing = await fetchExistingPoc(visitId, sectionId);
        } catch (e) {
            // Distinguish 404 (no POC row → "none") from network failures
            // (propagate so the focus effect can show an error CTA).
            if (e instanceof ApiError && e.kind === "http" && e.status === 404) {
                return { status: "none", epoch };
            }
            throw e;
        }
        if (!mountedRef.current || loadPocEpochRef.current !== epoch) {
            return { status: "none", epoch };
        }
        setPocMode({ pocMode: existing.pocMode, rnMayDecline: existing.rnMayDecline });
        if (existing.status === "generated") {
            // Defensive: a "generated" payload with no duties or no pocId is a
            // server bug. Surface, don't silently render an empty list that
            // traps the RN at the 5-duty minimum.
            if (!existing.pocId || existing.duties.length === 0) {
                setError("Plan of Care is missing duties — please regenerate.");
                return { status: "failed", epoch };
            }
            const serverDuties = existing.duties as MergedPocDuty[];
            const serverInstructions = existing.specialInstructions ?? "";

            // Capture AI baseline once per pocId. Wire pocId is a number at
            // runtime but typed string in PocData — normalize via String().
            // Snapshot `isSamePoc` BEFORE the ref flip so the dirty-guard
            // below can tell whether this load is "same generation" or
            // "server regenerated under us".
            const pocIdStr = String(existing.pocId);
            const isSamePoc = aiBaselineForPocIdRef.current === pocIdStr;
            if (!isSamePoc) {
                aiBaselineRef.current = new Map(
                    serverDuties.map((d) => [
                        dutyKey(d),
                        {
                            selected: d.aiSuggested,
                            frequency: d.agencyDefaultFrequency ?? d.frequency
                        }
                    ])
                );
                aiBaselineForPocIdRef.current = pocIdStr;
            }

            const dutiesSnap = serverDuties.map(toDutySnapshot);

            // Dirty guard: preserve in-progress RN edits ONLY when this load
            // returned the SAME pocId. If the server regenerated under us
            // (different pocId), the local edits reference codes from the
            // OLD generation — preserving them would wedge the next autosave
            // (sending OLD duty names against the NEW pocId). When pocId
            // changes, we must overwrite local state.
            if (isSamePoc && savedSnapshotRef.current && isDirtyRef.current()) {
                savedSnapshotRef.current = {
                    duties: dutiesSnap,
                    specialInstructions: serverInstructions
                };
                setPocId(existing.pocId);
                return { status: "generated", epoch };
            }
            setPocId(existing.pocId);
            setDuties(serverDuties);
            setSpecialInstructions(serverInstructions);
            savedSnapshotRef.current = {
                duties: dutiesSnap,
                specialInstructions: serverInstructions
            };
            return { status: "generated", epoch };
        }
        if (existing.status === "failed") {
            setError(existing.failureReason ?? "Generation failed.");
            return { status: "failed", epoch };
        }
        if (existing.status === "stale") {
            return { status: "stale", epoch };
        }
        if (existing.status === "not_required") {
            const assessment = existing.pocUpdateAssessment ?? null;
            setPocUpdateAssessment(assessment);
            setPocUpdateReason(assessment?.reasonText ?? "");
            return { status: "not_required", epoch };
        }
        return { status: "pending", epoch };
    }, [visitId, sectionId]);

    // ── Load on every focus (mount + back-stack pop) ──
    useFocusEffect(
        useCallback(() => {
            let cancelled = false;
            async function refresh() {
                try {
                    const { status, epoch } = await loadPoc();
                    if (cancelled || !mountedRef.current) {
                        return;
                    }
                    if (loadPocEpochRef.current !== epoch) {
                        return; // newer loadPoc raced ahead
                    }
                    if (status === "generated") {
                        setPhase("ready");
                    } else if (status === "not_required") {
                        setGenerationStartedAt(null);
                        setPhase("notRequired");
                    } else if (status === "stale") {
                        // The nurse went back and changed an assessment answer,
                        // so the server dropped the plan built on the old one.
                        setGenerationStartedAt(null);
                        setPhase("stale");
                    } else if (status === "pending") {
                        setPhase("generating");
                    } else if (status === "failed") {
                        setPhase("error");
                    } else {
                        setPhase("idle");
                    }
                } catch (e) {
                    if (!cancelled && mountedRef.current) {
                        setError(e instanceof Error ? e.message : "Failed to load existing POC");
                        setPhase("idle");
                    }
                }
            }
            refresh();
            return () => {
                cancelled = true;
            };
        }, [loadPoc])
    );

    // ── Generate handler ──
    const handleGenerate = useCallback(async () => {
        if (readOnly) {
            setError(readOnlyReason);
            return;
        }
        // Don't double-fire while a generation is already in flight. The
        // PocEmptyState button doesn't disable on phase change, so two quick
        // taps would otherwise hit POST twice (server coalesces, but client
        // UX is confusing).
        if (phase === "generating") {
            return;
        }
        setPhase("generating");
        setGenerationStartedAt(Date.now());
        setError(null);
        try {
            await generatePocDuties(visitId, sectionId);
            // Server returned 202; the worker now runs async. The
            // poc:generated WS event (below) flips phase to "ready". If WS is
            // missed, the focus effect re-runs loadPoc on next focus.
        } catch (e) {
            if (mountedRef.current) {
                setGenerationStartedAt(null);
                setError(
                    e instanceof Error ? e.message : "Failed to start Plan of Care generation"
                );
                setPhase("error");
            }
        }
    }, [phase, readOnly, readOnlyReason, visitId, sectionId]);

    // ── WebSocket subscriptions ──
    // Belt-and-suspenders: Props.visitId is typed `string`, so the `== null`
    // checks below look dead from TS. They aren't: a transient
    // deep-link/race mount can land here with `undefined`, and if
    // eventData.visitId is ALSO undefined for any reason, String() would
    // collapse both sides to "undefined" and we'd act on an unrelated
    // visit's event. Do NOT delete these null-checks even if TS says they're
    // dead.
    useWebSocketEvent("poc:generated", (eventData) => {
        if (visitId == null || eventData.visitId == null) {
            return;
        }
        if (String(eventData.visitId) !== String(visitId)) {
            return;
        }
        void loadPoc().then(({ status, epoch }) => {
            if (!mountedRef.current || loadPocEpochRef.current !== epoch) {
                return;
            }
            if (status === "generated") {
                setGenerationStartedAt(null);
                setPhase("ready");
            } else if (status === "not_required") {
                setGenerationStartedAt(null);
                setPhase("notRequired");
            }
        });
    });

    useWebSocketEvent("poc:failed", (eventData) => {
        if (visitId == null || eventData.visitId == null) {
            return;
        }
        if (String(eventData.visitId) !== String(visitId)) {
            return;
        }
        if (!mountedRef.current) {
            return;
        }
        setGenerationStartedAt(null);
        setError(eventData.reason ?? "Generation failed.");
        setPhase("error");
    });

    // ── WS reconnect catch-up ──
    // The WS provider disconnects on AppState=background and reconnects on
    // active. When that reconnect lands, re-fetch POC state so we catch up
    // on any event that fired while we were offline. Gated on
    // phase !== "ready" so the dirty-ref guard retains priority for users
    // in mid-edit.
    const { isConnected } = useWebSocket();
    const wasConnectedRef = useRef(isConnected);
    useEffect(() => {
        if (isConnected && !wasConnectedRef.current && phase !== "ready") {
            void loadPoc().then(({ status, epoch }) => {
                if (!mountedRef.current || loadPocEpochRef.current !== epoch) {
                    return;
                }
                if (status === "generated") {
                    setPhase("ready");
                } else if (status === "not_required") {
                    setPhase("notRequired");
                } else if (status === "failed") {
                    setPhase("error");
                }
            });
        }
        wasConnectedRef.current = isConnected;
    }, [isConnected, phase, loadPoc]);

    // ── Poll-while-generating fallback ──
    // The poc:generated WS push is best-effort: a dropped/half-open ("zombie")
    // socket swallows it with no replay (prod incident 2026-06-05 — see the RCA
    // doc). The three recoveries above all miss the case the RN actually hit:
    // focus-refetch needs a navigation/focus event (she never left the screen),
    // the WS handler needs the event to arrive (it was lost), and the
    // WS-reconnect catch-up needs isConnected to flip false→true (a zombie
    // socket never fires onclose, so it stayed "connected"). While generating,
    // poll the server status so the screen converges regardless of socket
    // liveness. Reuses loadPoc and its epoch/mounted race guards, so this is
    // just one more trigger feeding the same serialized fetch.
    usePollWhileProcessing({
        active: phase === "generating",
        intervalMs: 4000,
        timeoutMs: 4 * 60 * 1000,
        poll: loadPoc,
        isTerminal: ({ status }) =>
            status === "generated" || status === "failed" || status === "not_required",
        onResult: ({ status, epoch }) => {
            if (!mountedRef.current || loadPocEpochRef.current !== epoch) {
                return;
            }
            if (status === "generated") {
                setGenerationStartedAt(null);
                setPhase("ready");
            } else if (status === "not_required") {
                setGenerationStartedAt(null);
                setPhase("notRequired");
            } else if (status === "failed") {
                setPhase("error");
            }
        },
        onTimeout: () => {
            if (!mountedRef.current) {
                return;
            }
            setError("This is taking longer than expected. Tap to try again.");
            setPhase("error");
        }
    });

    // ── Autosave ──
    // performAutosave is recreated whenever pocId / sigModalVisible / saving
    // change, but it reads current duties / specialInstructions via refs so
    // the drift check at end-of-save compares to the LATEST state, not the
    // captured-at-start values.
    const performAutosave = useCallback(async () => {
        if (!pocId || !mountedRef.current) {
            return;
        }
        // Don't autosave while signature modal is open or confirm is in
        // flight — would race the confirm.
        if (sigModalVisible || saving) {
            return;
        }
        const epoch = ++autosaveEpochRef.current;
        const startDuties = dutiesRef.current;
        const startInstructions = specialInstructionsRef.current;
        const dutiesSnap = startDuties.map(toDutySnapshot);
        setAutosaveStatus("saving");
        try {
            await savePocDuties(visitId, pocId, dutiesSnap, startInstructions);
            if (!mountedRef.current || autosaveEpochRef.current !== epoch) {
                return;
            }
            // Drift check: only update savedSnapshotRef when local hasn't
            // changed since this save started (otherwise we'd flip dirty to
            // clean despite newer edits).
            const currentDuties = dutiesRef.current;
            const currentSnap = currentDuties.map(toDutySnapshot);
            const drifted =
                currentSnap.length !== dutiesSnap.length ||
                currentSnap.some((d, i) => !dutySnapshotsEqual(d, dutiesSnap[i])) ||
                specialInstructionsRef.current !== startInstructions;
            if (!drifted) {
                savedSnapshotRef.current = {
                    duties: dutiesSnap,
                    specialInstructions: startInstructions
                };
            }
            setAutosaveStatus("saved");
            setTimeout(() => {
                if (mountedRef.current && autosaveEpochRef.current === epoch) {
                    setAutosaveStatus("idle");
                }
            }, 2000);
        } catch {
            if (mountedRef.current && autosaveEpochRef.current === epoch) {
                setAutosaveStatus("error");
            }
        }
    }, [pocId, visitId, sigModalVisible, saving]);

    // Ref pattern keeps scheduleAutosave (and the handlers that depend on it)
    // stable across renders even though performAutosave's deps change.
    const performAutosaveRef = useRef(performAutosave);
    performAutosaveRef.current = performAutosave;

    const scheduleAutosave = useCallback(() => {
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
        }
        autosaveTimerRef.current = setTimeout(() => {
            void performAutosaveRef.current();
        }, 1500);
    }, []);

    // ── Duty state handlers ──
    const handleToggleDuty = useCallback(
        (index: number) => {
            setDuties((prev) =>
                prev.map((d, i) => {
                    if (i !== index) {
                        return d;
                    }
                    const next = { ...d, selected: !d.selected };
                    return isDutyOverridden(next)
                        ? next
                        : { ...next, rnChangeReasonCodes: [], rnChangeReasonNote: null };
                })
            );
            scheduleAutosave();
        },
        [scheduleAutosave]
    );

    const handleChangeFrequency = useCallback(
        (index: number, frequency: PocFrequency) => {
            setDuties((prev) =>
                prev.map((d, i) => {
                    if (i !== index) {
                        return d;
                    }
                    const next = { ...d, frequency };
                    return isDutyOverridden(next)
                        ? next
                        : { ...next, rnChangeReasonCodes: [], rnChangeReasonNote: null };
                })
            );
            scheduleAutosave();
        },
        [scheduleAutosave]
    );

    const handleInstructionsChange = useCallback(
        (text: string) => {
            setSpecialInstructions(text);
            scheduleAutosave();
        },
        [scheduleAutosave]
    );

    const handleChangeDutyExplanation = useCallback(
        (
            index: number,
            explanation: {
                rnChangeReasonCodes: PocDutyChangeReason[];
                rnChangeReasonNote: string | null;
            }
        ) => {
            setDuties((prev) =>
                prev.map((d, i) =>
                    i === index
                        ? {
                              ...d,
                              rnChangeReasonCodes: explanation.rnChangeReasonCodes,
                              rnChangeReasonNote: explanation.rnChangeReasonNote
                          }
                        : d
                )
            );
            scheduleAutosave();
        },
        [scheduleAutosave]
    );

    // Batched Select All / Deselect All — one setDuties call, one autosave.
    const handleSetCategorySelection = useCallback(
        (keys: Set<string>, selected: boolean) => {
            setDuties((prev) =>
                prev.map((d) => {
                    if (!keys.has(dutyKey(d))) {
                        return d;
                    }
                    const next = { ...d, selected };
                    return isDutyOverridden(next)
                        ? next
                        : { ...next, rnChangeReasonCodes: [], rnChangeReasonNote: null };
                })
            );
            scheduleAutosave();
        },
        [scheduleAutosave]
    );

    // ── Confirm flow ──
    const handleConfirm = useCallback(() => {
        // Guard against double-tap before the signature modal animates in.
        if (sigModalVisible || saving) {
            return;
        }
        if (!pocId) {
            return;
        }
        const selectedCount = duties.filter((d) => d.selected).length;
        if (selectedCount < 5) {
            // The bottom-bar copy in PocDutyList explains why the button is
            // disabled; no need to set an error here.
            return;
        }
        const missingExplanation = duties.some(
            (duty) => isDutyOverridden(duty) && !hasDutyChangeExplanation(duty)
        );
        if (missingExplanation) {
            setConfirmError({
                message: "Please explain why are you making each RN duty change before confirming.",
                retryable: false
            });
            return;
        }
        setConfirmError(null);
        setSigModalVisible(true);
    }, [pocId, duties, sigModalVisible, saving]);

    const performSaveAndConfirm = useCallback(
        async (signatureData: string) => {
            if (!pocId) {
                return;
            }
            setSaving(true);
            setConfirmError(null);
            try {
                await savePocDuties(
                    visitId,
                    pocId,
                    duties.map(toDutySnapshot),
                    specialInstructions
                );
                await confirmPoc(visitId, pocId, signatureData);
                if (mountedRef.current) {
                    // Update saved snapshot so isDirty() correctly returns
                    // false post-confirm.
                    savedSnapshotRef.current = {
                        duties: duties.map(toDutySnapshot),
                        specialInstructions
                    };
                    setPendingSignature(null);
                    onComplete();
                }
            } catch (e) {
                if (!mountedRef.current) {
                    return;
                }
                // If confirmPoc failed but the server may have committed,
                // re-fetch and check confirmedByRnAt before showing the
                // retry banner. Avoids double-confirm on lost-response.
                if (e instanceof ApiError && e.kind !== "abort") {
                    // Bound the already-confirmed check tightly. The default
                    // request timeout is 120s; if the original confirm
                    // failure was a slow network, this second call could
                    // hang for another 2 minutes before the user sees the
                    // retry banner. 8s is enough for any healthy response,
                    // short enough to be tolerable as added latency on a
                    // failure path.
                    const stillNeedsConfirm = await fetchExistingPoc(visitId, sectionId, {
                        timeoutMs: 8000
                    })
                        .then((r) => r.confirmedByRnAt == null)
                        .catch(() => true);
                    if (!stillNeedsConfirm && mountedRef.current) {
                        setPendingSignature(null);
                        onComplete();
                        return;
                    }
                }
                const retryable =
                    e instanceof ApiError && (e.kind === "network" || (e.status ?? 0) >= 500);
                setConfirmError({
                    message:
                        e instanceof ApiError && e.kind === "network"
                            ? "Connection lost. Tap retry to finish confirming — no need to re-sign."
                            : e instanceof Error
                              ? e.message
                              : "Failed to confirm Plan of Care.",
                    retryable
                });
            } finally {
                if (mountedRef.current) {
                    setSaving(false);
                }
            }
        },
        [pocId, duties, specialInstructions, visitId, sectionId, onComplete]
    );

    const handleSignatureDone = useCallback(
        async (signatureData: string) => {
            setSigModalVisible(false);
            setPendingSignature(signatureData);
            await performSaveAndConfirm(signatureData);
        },
        [performSaveAndConfirm]
    );

    const handleRetryConfirm = useCallback(async () => {
        if (!pendingSignature) {
            return;
        }
        await performSaveAndConfirm(pendingSignature);
    }, [performSaveAndConfirm, pendingSignature]);

    const handleConfirmNoUpdate = useCallback(async () => {
        if (!pocUpdateAssessment || saving || readOnly) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await confirmPocUpdateAssessment(
                visitId,
                sectionId,
                pocUpdateAssessment.id,
                pocUpdateReason
            );
            if (mountedRef.current) {
                onComplete();
            }
        } catch (e) {
            if (mountedRef.current) {
                setError(e instanceof Error ? e.message : "Failed to confirm POC decision.");
                setPhase("error");
            }
        } finally {
            if (mountedRef.current) {
                setSaving(false);
            }
        }
    }, [pocUpdateAssessment, saving, readOnly, visitId, sectionId, pocUpdateReason, onComplete]);

    const handleRejectNoUpdate = useCallback(async () => {
        if (saving || readOnly) {
            return;
        }
        setSaving(true);
        setPhase("generating");
        setGenerationStartedAt(Date.now());
        setError(null);
        try {
            await generatePocDuties(visitId, sectionId, { forceGenerate: true });
        } catch (e) {
            if (mountedRef.current) {
                setGenerationStartedAt(null);
                setError(
                    e instanceof Error ? e.message : "Failed to start Plan of Care generation"
                );
                setPhase("error");
            }
        } finally {
            if (mountedRef.current) {
                setSaving(false);
            }
        }
    }, [saving, readOnly, visitId, sectionId]);

    // ── Render by phase ──

    if (phase === "loading") {
        return (
            <View style={styles.centerContainer}>
                <ActivityIndicator size="large" color={colors.teal400} />
                <Text style={styles.loadingText}>Loading Plan of Care...</Text>
            </View>
        );
    }

    if (phase === "idle") {
        if (readOnly) {
            return (
                <View style={styles.centerContainer}>
                    <View style={styles.readOnlyIcon}>
                        <MaterialCommunityIcons
                            name="lock-outline"
                            size={40}
                            color={colors.info500}
                        />
                    </View>
                    <Text style={styles.errorTitle}>Plan of Care is view only</Text>
                    <Text style={styles.errorMessage}>{readOnlyReason}</Text>
                    <TouchableOpacity onPress={onBack} style={styles.backLink}>
                        <Text style={styles.backLinkText}>Go Back</Text>
                    </TouchableOpacity>
                </View>
            );
        }
        return <PocEmptyState onGenerate={handleGenerate} />;
    }

    if (phase === "stale") {
        return <PocEmptyState onGenerate={handleGenerate} variant="stale" />;
    }

    if (phase === "generating") {
        return <PocGeneratingAnimation startedAt={generationStartedAt ?? Date.now()} />;
    }

    if (phase === "notRequired") {
        return (
            <>
                <View style={styles.header}>
                    <TouchableOpacity
                        onPress={onBack}
                        accessibilityRole="button"
                        accessibilityLabel="Back"
                        hitSlop={12}
                        style={styles.headerBack}
                    >
                        <MaterialCommunityIcons
                            name="chevron-left"
                            size={22}
                            color={colors.teal500}
                        />
                        <Text style={styles.headerBackText}>Back</Text>
                    </TouchableOpacity>
                    <View style={styles.headerCenter}>
                        <Text style={styles.headerTitle} numberOfLines={1}>
                            {sectionDisplayName}
                        </Text>
                    </View>
                    <View style={styles.headerSpacer} />
                </View>
                <View style={styles.noUpdateContainer}>
                    <View style={styles.noUpdateIcon}>
                        <MaterialCommunityIcons
                            name="clipboard-check-outline"
                            size={42}
                            color={colors.teal500}
                        />
                    </View>
                    <Text style={styles.noUpdateTitle}>No POC update required</Text>
                    <Text style={styles.noUpdateMessage}>
                        Our validation shows this patient requires no updated Plan of Care. Confirm
                        the reason below, or reject and create the Plan of Care.
                    </Text>
                    <TextInput
                        value={pocUpdateReason}
                        onChangeText={setPocUpdateReason}
                        editable={!readOnly && !saving}
                        multiline
                        style={styles.noUpdateInput}
                        textAlignVertical="top"
                        placeholder="Reason"
                        placeholderTextColor={colors.textTertiary}
                    />
                    <TouchableOpacity
                        onPress={handleConfirmNoUpdate}
                        disabled={saving || readOnly || !pocUpdateAssessment}
                        activeOpacity={0.85}
                        style={styles.noUpdatePrimaryWrapper}
                    >
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={[
                                styles.noUpdatePrimary,
                                (saving || readOnly || !pocUpdateAssessment) &&
                                    styles.noUpdateDisabled
                            ]}
                        >
                            <MaterialCommunityIcons name="check" size={18} color="#FFFFFF" />
                            <Text style={styles.noUpdatePrimaryText}>
                                {saving ? "Confirming..." : "Confirm"}
                            </Text>
                        </LinearGradient>
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={handleRejectNoUpdate}
                        disabled={saving || readOnly}
                        style={styles.noUpdateSecondary}
                    >
                        <Text style={styles.noUpdateSecondaryText}>Reject and Create POC</Text>
                    </TouchableOpacity>
                </View>
            </>
        );
    }

    if (phase === "error") {
        return (
            <View style={styles.centerContainer}>
                <View style={styles.errorIcon}>
                    <MaterialCommunityIcons
                        name="alert-circle-outline"
                        size={44}
                        color={colors.error500}
                    />
                </View>
                <Text style={styles.errorTitle}>Something went wrong</Text>
                <Text style={styles.errorMessage}>{error}</Text>
                <TouchableOpacity
                    onPress={handleGenerate}
                    activeOpacity={0.85}
                    style={styles.retryBtnWrapper}
                >
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.retryBtn}
                    >
                        <MaterialCommunityIcons name="refresh" size={18} color="#FFFFFF" />
                        <Text style={styles.retryBtnText}>Try Again</Text>
                    </LinearGradient>
                </TouchableOpacity>
                <TouchableOpacity onPress={onBack} style={styles.backLink}>
                    <Text style={styles.backLinkText}>Go Back</Text>
                </TouchableOpacity>
            </View>
        );
    }

    // phase === "ready"
    return (
        <>
            <View style={styles.header}>
                <TouchableOpacity
                    onPress={onBack}
                    accessibilityRole="button"
                    accessibilityLabel="Back"
                    hitSlop={12}
                    style={styles.headerBack}
                >
                    <MaterialCommunityIcons name="chevron-left" size={22} color={colors.teal500} />
                    <Text style={styles.headerBackText}>Back</Text>
                </TouchableOpacity>
                <View style={styles.headerCenter}>
                    <Text style={styles.headerTitle} numberOfLines={1}>
                        {sectionDisplayName}
                    </Text>
                </View>
                <View style={styles.headerSpacer} />
            </View>
            {confirmError != null && (
                <View style={styles.confirmErrorBanner}>
                    <Text style={styles.confirmErrorText}>{confirmError.message}</Text>
                    {confirmError.retryable && pendingSignature != null ? (
                        <TouchableOpacity
                            onPress={handleRetryConfirm}
                            disabled={saving}
                            accessibilityRole="button"
                            accessibilityLabel="Retry confirm Plan of Care"
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                            style={styles.confirmErrorRetry}
                        >
                            <Text style={styles.confirmErrorRetryText}>
                                {saving ? "Retrying…" : "Retry"}
                            </Text>
                        </TouchableOpacity>
                    ) : (
                        <TouchableOpacity
                            onPress={() => setConfirmError(null)}
                            accessibilityRole="button"
                            accessibilityLabel="Dismiss error"
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                            style={styles.confirmErrorDismiss}
                        >
                            <Text style={styles.confirmErrorDismissText}>Dismiss</Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}
            <PocDutyList
                duties={duties}
                carryForward={pocMode?.pocMode === "carry_forward"}
                specialInstructions={specialInstructions}
                aiBaseline={aiBaselineRef.current}
                autosaveStatus={autosaveStatus}
                onToggleDuty={handleToggleDuty}
                onChangeFrequency={handleChangeFrequency}
                onChangeDutyExplanation={handleChangeDutyExplanation}
                onChangeInstructions={handleInstructionsChange}
                onSetCategorySelection={handleSetCategorySelection}
                onConfirm={handleConfirm}
                saving={saving}
                readOnly={readOnly}
                readOnlyReason={readOnlyReason}
            />
            <SignatureModal
                visible={sigModalVisible}
                title="Plan of Care Signature"
                onDone={handleSignatureDone}
                onClose={() => setSigModalVisible(false)}
            />
        </>
    );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.md,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    headerBack: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        minWidth: 60
    },
    headerBackText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.teal500
    },
    headerCenter: {
        flex: 1,
        alignItems: "center"
    },
    headerTitle: {
        fontSize: 18,
        fontFamily: fonts.bold,
        lineHeight: 23,
        color: colors.textPrimary
    },
    headerSpacer: {
        minWidth: 60
    },
    centerContainer: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 32
    },
    loadingText: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginTop: spacing.base
    },
    errorIcon: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: colors.error50,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20
    },
    readOnlyIcon: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: colors.info50,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20
    },
    errorTitle: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: 8
    },
    errorMessage: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 20,
        maxWidth: 280,
        marginBottom: 24
    },
    retryBtnWrapper: {
        width: "100%",
        maxWidth: 220
    },
    retryBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 14,
        borderRadius: radius.lg,
        ...shadows.brand
    },
    retryBtnText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    backLink: {
        marginTop: 16,
        padding: 8
    },
    backLinkText: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary
    },
    noUpdateContainer: {
        flex: 1,
        paddingHorizontal: spacing.xl,
        paddingTop: 48,
        backgroundColor: colors.background
    },
    noUpdateIcon: {
        width: 76,
        height: 76,
        borderRadius: 38,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18
    },
    noUpdateTitle: {
        fontSize: 22,
        fontFamily: fonts.bold,
        lineHeight: 28,
        color: colors.textPrimary,
        marginBottom: 8
    },
    noUpdateMessage: {
        fontSize: 14,
        fontFamily: fonts.regular,
        lineHeight: 21,
        color: colors.textSecondary,
        marginBottom: 18
    },
    noUpdateInput: {
        minHeight: 150,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.md,
        padding: 14,
        backgroundColor: colors.surface,
        color: colors.textPrimary,
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 18
    },
    noUpdatePrimaryWrapper: {
        width: "100%"
    },
    noUpdatePrimary: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 15,
        borderRadius: radius.lg,
        ...shadows.brand
    },
    noUpdateDisabled: {
        opacity: 0.5
    },
    noUpdatePrimaryText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    noUpdateSecondary: {
        alignItems: "center",
        paddingVertical: 16,
        marginTop: 8
    },
    noUpdateSecondaryText: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.textSecondary
    },
    confirmErrorBanner: {
        marginHorizontal: spacing.base,
        marginTop: spacing.base,
        padding: 12,
        backgroundColor: colors.error50,
        borderColor: colors.error500,
        borderWidth: 1,
        borderRadius: radius.md,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12
    },
    confirmErrorText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.error500
    },
    confirmErrorRetry: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: colors.error500,
        borderRadius: radius.full
    },
    confirmErrorRetryText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    confirmErrorDismiss: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderWidth: 1,
        borderColor: colors.error500,
        borderRadius: radius.full,
        backgroundColor: "transparent"
    },
    confirmErrorDismissText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.error500
    }
});
